import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Chip,
  LinearProgress,
  TablePagination,
  FormHelperText,
  Toolbar,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import MaterialTable from 'material-table'
import { Sidescroll as SidescrollBase } from 'components'
import { useIntl } from 'react-intl'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import { useAuditLogs } from 'services/queries/use-audit-logs'
import {
  AuditLogEntry,
  AuditLogParams,
  AuditLogSortField,
} from 'services/cm-api/get-audit-logs'
import { icons, localization } from './audit-log-viewer.model'
import { theme as defaultTheme } from 'styles/theme'

// Cast to avoid TypeScript strict prop requirements from JS component
const Sidescroll: React.FC<{
  conditionalySidescroll?: boolean
  maxWidth?: string
  children: React.ReactNode
}> = SidescrollBase as any
const Table: React.FC<Record<string, unknown>> = MaterialTable as any

type AuditLogViewerProps = {
  customerId: number | string
  showTitle?: boolean
}

const REASON_OPTIONS = ['CDD', 'CREDIT_RISK', 'SANCTIONS_SCREENING']

// Sanitize search input to prevent injection attacks
// Allow alphanumeric, spaces, hyphens, and basic punctuation
const sanitizeSearchInput = (input: string): string => {
  return input.replace(/[^a-zA-Z0-9\s\-.,]/g, '').slice(0, 100)
}

const formatTimestamp = (timestamp: string): string => {
  return moment.utc(timestamp).local().format('DD MMM YYYY, HH:mm')
}

const AuditLogViewer = ({
  customerId,
  showTitle = true,
}: AuditLogViewerProps) => {
  const currentTheme = useTheme()
  const reactIntl = useIntl()
  const messages = reactIntl.messages

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [reasonFilter, setReasonFilter] = useState<string>('')
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [sortField, setSortField] = useState<AuditLogSortField>('timestamp')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Debounce search input
  const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = sanitizeSearchInput(event.target.value)
      setSearchTerm(value)

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }

      debounceTimeout.current = setTimeout(() => {
        setDebouncedSearch(value)
        setPage(0)
      }, 300)
    },
    [],
  )

  // Check if date range is valid (end date should not be before start date)
  const isDateRangeValid =
    !startDate || !endDate || endDate.isSameOrAfter(startDate, 'day')

  const queryParams: AuditLogParams = useMemo(
    () => ({
      page: page + 1,
      page_size: rowsPerPage,
      search: debouncedSearch ? debouncedSearch : undefined,
      reason: reasonFilter ? reasonFilter : undefined,
      date_from:
        startDate && isDateRangeValid
          ? startDate.format('YYYY-MM-DD')
          : undefined,
      date_to:
        endDate && isDateRangeValid ? endDate.format('YYYY-MM-DD') : undefined,
      sort_by: sortField,
      sort_order: sortOrder,
    }),
    [
      page,
      rowsPerPage,
      debouncedSearch,
      reasonFilter,
      startDate,
      endDate,
      sortField,
      sortOrder,
      isDateRangeValid,
    ],
  )

  const { data, isFetching } = useAuditLogs(
    customerId,
    queryParams,
    isDateRangeValid,
  )

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleReasonChange = (event: any) => {
    setReasonFilter(event.target.value)
    setPage(0)
  }

  const handleStartDateChange = (date: Moment | null) => {
    setStartDate(date)
    setPage(0)
  }

  const handleEndDateChange = (date: Moment | null) => {
    setEndDate(date)
    setPage(0)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setDebouncedSearch('')
    setReasonFilter('')
    setStartDate(null)
    setEndDate(null)
    setPage(0)
  }

  const hasActiveFilters =
    debouncedSearch || reasonFilter || startDate || endDate

  // Map column index to sortable field (only sortable columns)
  const sortableFieldMap: Record<number, AuditLogSortField> = {
    0: 'timestamp',
    1: 'customer_id',
    2: 'user_name',
    4: 'reason',
  }

  const handleOrderChange = (
    orderBy: number,
    orderDirection: 'asc' | 'desc',
  ) => {
    const field = sortableFieldMap[orderBy]
    if (field) {
      setSortField(field)
      setSortOrder(orderDirection)
      setPage(0)
    }
  }

  const columns = [
    {
      title: 'Timestamp',
      field: 'timestamp',
      render: (rowData: AuditLogEntry) => formatTimestamp(rowData.timestamp),
      defaultSort:
        sortField === 'timestamp' ? (sortOrder as 'asc' | 'desc') : undefined,
    },
    {
      title: 'Customer',
      field: 'customer_id',
      defaultSort:
        sortField === 'customer_id' ? (sortOrder as 'asc' | 'desc') : undefined,
    },
    {
      title: 'User',
      field: 'user_name',
      defaultSort:
        sortField === 'user_name' ? (sortOrder as 'asc' | 'desc') : undefined,
      render: (rowData: AuditLogEntry) => (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={500}>
            {rowData.user_name || '-'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {rowData.user_email || `ID: ${rowData.user_id}`}
          </Typography>
        </Stack>
      ),
    },
    {
      title: 'Viewed Company',
      field: 'viewed_company',
      sorting: false,
      render: (rowData: AuditLogEntry) => (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={500}>
            {rowData.viewed_company?.company_name || '-'}
          </Typography>
          {rowData.viewed_company?.local_id && (
            <Typography variant="caption" color="textSecondary">
              ID: {rowData.viewed_company.local_id}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      title: 'Reason',
      field: 'reason',
      defaultSort:
        sortField === 'reason' ? (sortOrder as 'asc' | 'desc') : undefined,
      render: (rowData: AuditLogEntry) => (
        <Chip
          label={rowData.reason}
          size="medium"
          variant="outlined"
          sx={{ '& .MuiChip-label': { fontSize: '12px' } }}
        />
      ),
    },
    {
      title: 'CVR',
      field: 'cvr',
      sorting: false,
    },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Sidescroll conditionalySidescroll maxWidth={'819px'}>
        <ThemeProvider theme={currentTheme}>
          <Table
            isLoading={isFetching}
            options={{
              pageSize: rowsPerPage,
              pageSizeOptions: [20, 50, 100],
              search: false,
              sorting: true,
              draggable: false,
              paging: true,
              loadingType: 'linear',
            }}
            onOrderChange={handleOrderChange}
            components={{
              Container: (props: any) => <Box {...props} />,
              Toolbar: (props: any) => (
                <Box>
                  {isFetching && <LinearProgress />}
                  <Toolbar disableGutters style={{ paddingRight: 0 }}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      justifyContent="flex-end"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ width: '100%', pt: 4, px: 4 }}
                    >
                      <TextField
                        size="small"
                        placeholder="Search by customer name, CVR, or company ID"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        sx={{ width: 400, flexShrink: 0 }}
                        helperText=" "
                        FormHelperTextProps={{
                          style: { fontSize: '10px' },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FontAwesomeIcon icon={faSearch} />
                            </InputAdornment>
                          ),
                        }}
                      />

                      <FormControl
                        size="small"
                        sx={{ width: 180, flexShrink: 0 }}
                      >
                        <InputLabel>Reason</InputLabel>
                        <Select
                          value={reasonFilter}
                          label="Reason"
                          onChange={handleReasonChange}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiList-root': {
                                  display: 'flex',
                                  flexDirection: 'column',
                                },
                                '& .MuiMenuItem-root': {
                                  padding: '10px 16px',
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value="">All</MenuItem>
                          {REASON_OPTIONS.map((reason) => (
                            <MenuItem key={reason} value={reason}>
                              {reason}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText style={{ fontSize: '10px' }}>
                          {' '}
                        </FormHelperText>
                      </FormControl>

                      <DatePicker
                        format="DD.MM.YYYY"
                        label="Start Date"
                        value={startDate}
                        onChange={handleStartDateChange}
                        maxDate={moment()}
                        slotProps={{
                          textField: {
                            size: 'small',
                            helperText: ' ',
                            FormHelperTextProps: {
                              style: { fontSize: '10px' },
                            },
                            sx: { width: 180, flexShrink: 0 },
                          },
                        }}
                      />

                      <DatePicker
                        format="DD.MM.YYYY"
                        label="End Date"
                        value={endDate}
                        onChange={handleEndDateChange}
                        maxDate={moment()}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: !isDateRangeValid,
                            helperText: !isDateRangeValid
                              ? 'Cannot be before start date'
                              : ' ',
                            FormHelperTextProps: {
                              style: { fontSize: '10px' },
                            },
                            sx: { width: 180, flexShrink: 0 },
                          },
                        }}
                      />

                      {hasActiveFilters && (
                        <Box sx={{ mb: '16px' }}>
                          <Chip
                            label="Clear filters"
                            onDelete={clearFilters}
                            onClick={clearFilters}
                            size="medium"
                            color="secondary"
                            sx={{ '& .MuiChip-label': { fontSize: '14px' } }}
                          />
                        </Box>
                      )}
                    </Stack>
                  </Toolbar>
                </Box>
              ),
              Pagination: (props: any) => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    pl: 2,
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & p':
                      {
                        fontSize: '14px',
                      },
                    '& [class*="MuiTablePagination-select"]': {
                      paddingBottom: 0,
                    },
                  }}
                >
                  <TablePagination
                    rowsPerPageOptions={[20, 50, 100]}
                    component="div"
                    count={
                      isDateRangeValid
                        ? data?.pagination?.total_records ?? 0
                        : 0
                    }
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    showFirstButton
                    showLastButton
                    slotProps={{
                      select: {
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              '& .MuiList-root': {
                                display: 'flex',
                                flexDirection: 'column',
                              },
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              ),
            }}
            localization={localization(messages, isFetching)}
            elevation={0}
            title={showTitle ? 'Beneficial Owners Audit Logs' : ''}
            columns={columns}
            icons={icons}
            data={isDateRangeValid ? data?.data ?? [] : []}
          />
        </ThemeProvider>
      </Sidescroll>
    </LocalizationProvider>
  )
}

export default AuditLogViewer
